import React, { ReactNode } from 'react';
import { useShareUrls } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useTranslation } from 'react-i18next';
import { ShareContextProvider } from '../../context/ShareContext';
import { isCharity } from '../../utils/typeGuards';
import { generateEmailBody } from '../../utils/generateEmailBody';

interface TeamsShareProps {
  children: ReactNode;
}

export function TeamsShareProvider({ children }: TeamsShareProps) {
  const { data: basePageData } = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });

  const title = basePageData?.page?.title;
  const beneficiaries = basePageData?.page?.relationships?.beneficiaries;

  const charities = basePageData?.page?.relationships?.beneficiaries.nodes.filter(
    isCharity,
  );
  const charityName = charities?.length ? charities[0].name : null;
  const charityCountryCode = charities?.length
    ? charities[0].countryCode
    : null;
  const isNonUKCharity = charityCountryCode !== 'GB';

  const shareMessage = t(
    'team.shareMessage',
    'Please support {{title}} who are fundraising for {{charityName}} on JustGiving.',
    {
      replace: {
        title,
        charityName: charityName,
      },
      count: beneficiaries?.totalCount ?? 0,
    },
  );

  const giftAidMessage = isNonUKCharity
    ? ''
    : t('common.charityGiftAid', {
        charityName: charityName,
        count: (beneficiaries && beneficiaries.totalCount) || 0,
      });

  const emailMapper = (url: string) => ({
    subject: t('team.shareTitle', '{{title}} are fundraising!', {
      replace: { title },
    }),
    body: generateEmailBody({
      greeting: t('common.greeting'),
      message: shareMessage,
      shareUrl: url,
      giftAidMessage,
      thanks: t('common.emailThanks'),
    }),
  });

  const whatsAppAndSmsMapper = (url: string) => `${shareMessage} ${url}`;

  const twitterMapper = () => shareMessage;

  const shareItems = useShareUrls({
    email: emailMapper,
    sms: whatsAppAndSmsMapper,
    twitter: twitterMapper,
    whatsApp: whatsAppAndSmsMapper,
  });

  return (
    <ShareContextProvider value={{ shareItems }}>
      {children}
    </ShareContextProvider>
  );
}
